import { Box, Button, FlexColumn, FlexRow, OverflowText, Popover } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { POPOVER_RIGHT_START } from "designSystem/styles/popover-positions";
import { FONT_SEMIBOLD } from "designSystem/theme";
import { preventDefaultBehaviour } from "designSystem/utilities/prevent-default-behavior";
import { observer } from "mobx-react-lite";
import React, { FC } from "react";
import { DUMMY } from "src/c8-platform/type/node";
import { cnl } from "src/c8-platform/type/value";
import { useCustomViewDialogStore } from "../CustomViewDialogStore";
import { AccordionSummaryCommonProps, CategoryIconMap } from "./CustomViewDialogLeftPanelAccordion";

interface DefaultViewListProps {
	/** party url, if it's global, then the value is DUMMY */
	partyUrl: cnl;
}

const DefaultViewList: FC<DefaultViewListProps> = observer((props: DefaultViewListProps) => {
	const { partyUrl } = props;
	const {
		defaultCustomViewOfSelectedGroupMap,
		selectDefaultCustomView,
	} = useCustomViewDialogStore();

	const defaultCustomViewOfSelectedGroup = defaultCustomViewOfSelectedGroupMap.get(partyUrl);

	const list = defaultCustomViewOfSelectedGroup?.map((defaultCustomView) => {
		const { category, group, url, name } = defaultCustomView;
		const onClickCallback = (e) => selectDefaultCustomView(e, defaultCustomView, partyUrl);

		return (
			<Box
				alignItems="center"
				key={url}
				p={2}
			>
				{group && (
					<FlexRow>
						<OverflowText bold text={group} />
					</FlexRow>
				)}
				<FlexRow>
					<Box alignContent="center" pr={1}>{CategoryIconMap[category]}</Box>
					<Button
						design="link"
						onClick={onClickCallback}
					>
						<OverflowText text={name} />
					</Button>
				</FlexRow>
			</Box>
		);
	});

	return (
		<>
			{list}
		</>
	);
});

interface DefaultViewPopupProps extends Pick<AccordionSummaryCommonProps, "customViewItem"> { }

const DefaultViewPopup: FC<DefaultViewPopupProps> = observer((props: DefaultViewPopupProps) => {
	const loc = useLocale();
	const { customViewItem } = props;
	const {
		getAnchorEl,
		setAnchorEl,
		getDefaultCustomViewInfo,
		handleCloseDefaultCustomViewPopup,
	} = useCustomViewDialogStore();

	const partyUrl = customViewItem?.url || DUMMY;
	const anchorEl = getAnchorEl(partyUrl);
	const open = !!anchorEl;

	/** open up the pop up */
	const handleClick = async (event: React.MouseEvent<HTMLButtonElement>, url: cnl): Promise<void> => {
		setAnchorEl(url, event.currentTarget);
		preventDefaultBehaviour(event);
		getDefaultCustomViewInfo(customViewItem);
	};

	const popupTitleResParams = [customViewItem?.name || loc.resource("CustomViewCategoryGlobal")];

	return (
		<>
			<Button
				design="link"
				fullWidth
				onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleClick(event, partyUrl)}
				res="CustomViewDesignDefaultView"
				sx={{ fontWeight: FONT_SEMIBOLD }}
			/>
			<Popover
				{...POPOVER_RIGHT_START}
				anchorEl={anchorEl}
				onClose={(event: React.MouseEvent<HTMLElement>, reason: "backdropClick" | "escapeKeyDown") => handleCloseDefaultCustomViewPopup(event, partyUrl)}
				open={open}
				slotProps={
					{
						paper: {
							sx: {
								width: 264,
								minHeight: 60,
							},
						},
					}
				}
			>
				<FlexColumn>
					<FlexRow p={2}>
						<OverflowText
							bold
							res="CustomViewDefaultPopupTitle"
							resParams={popupTitleResParams}
						/>
					</FlexRow>
					<DefaultViewList partyUrl={partyUrl} />
				</FlexColumn>
			</Popover>
		</>
	);
});

export default DefaultViewPopup;